// Shared bits for the refund engine admin UI (Master Data → Refund Engine, booking view, wallet).
// Plain Tailwind + the app's own components (Selection, Switch, Table, Modal) so it matches the dashboard.
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import moment from 'moment'
import { ExclamationIcon, InformationCircleIcon, XCircleIcon } from '@heroicons/react/solid'
import { clamp, compact, filter, get, includes, isBoolean, isNil, isObject, keys, map, pickBy, trim } from 'lodash'
import apiGet from 'lib/network/apiGet'
import Selection from 'components/molecules/Selection'

export const REFUND_TYPES = ['standard', 'free_cancellation', 'non_refundable']
export const REFUND_STATUSES = ['processing', 'pending_approval', 'credited', 'rejected', 'no_refund']
export const CANCELLED_BY = ['customer', 'company']

export const omr = value => (isNil(value) ? '-' : `OMR ${Number(value).toFixed(3)}`)
export const fmtDate = value => (value ? moment(value).format('DD MMM YYYY, HH:mm') : '-')
/** '' / null -> null, anything else -> Number (form state keeps raw input strings). */
export const toNumber = value => (isNil(value) || trim(String(value)) === '' ? null : Number(value))
export const isBlank = value => isNil(value) || trim(String(value)) === ''
/** Value for a native datetime-local input. */
export const toLocalInput = value => (value ? moment(value).format('YYYY-MM-DDTHH:mm') : '')

// ---- Styling (same classes as Master Data modals / booking view) ----

export const LABEL = 'block text-sm font-medium text-gray-700 mb-1'
export const inputClass = error =>
  classNames(
    'block w-full px-3 py-2 border rounded-md shadow-sm sm:text-sm focus:outline-none focus:ring-1 disabled:bg-gray-100 disabled:text-gray-500',
    error ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-dark-500 focus:border-dark-500'
  )

const BTN_VARIANTS = {
  primary: 'border-transparent text-white bg-dark-600 hover:bg-dark-700 focus:ring-dark-500',
  secondary: 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-dark-500',
  danger: 'border-transparent text-white bg-red-600 hover:bg-red-700 focus:ring-red-500',
}
export const btn = (variant = 'primary', small = false) =>
  classNames(
    'inline-flex items-center justify-center whitespace-nowrap border font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed',
    small ? 'px-2.5 py-1 text-xs' : 'px-4 py-2 text-sm',
    BTN_VARIANTS[variant]
  )

/** Card section: white panel with an optional header row. */
export const Panel = ({ title, right, className, bodyClassName = 'p-4 sm:p-6', children }) => (
  <section className={classNames('bg-white rounded-lg border border-gray-200 shadow-sm', className)}>
    {(title || right) && (
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 py-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {right}
      </div>
    )}
    <div className={bodyClassName}>{children}</div>
  </section>
)

const TONES = {
  amber: 'bg-amber-100 text-amber-800',
  green: 'bg-green-100 text-green-800',
  red: 'bg-red-100 text-red-800',
  blue: 'bg-blue-100 text-blue-800',
  purple: 'bg-purple-100 text-purple-800',
  gray: 'bg-gray-100 text-gray-800',
}
export const Badge = ({ tone = 'gray', className, children }) => (
  <span className={classNames('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap', TONES[tone], className)}>{children}</span>
)

const STATUS_TONES = { processing: 'blue', pending_approval: 'amber', credited: 'green', rejected: 'red', no_refund: 'gray' }
export const StatusBadge = ({ status }) => {
  const { t } = useTranslation()
  return status ? <Badge tone={STATUS_TONES[status]}>{t(`refundEngine.status.${status}`)}</Badge> : '-'
}
export const MethodBadge = ({ method }) => {
  const { t } = useTranslation()
  return method ? <Badge tone={method === 'gateway' || method === 'mixed' ? 'blue' : 'gray'}>{t(`refundEngine.method.${method}`)}</Badge> : '-'
}

const NOTICES = {
  info: ['bg-blue-50 border-blue-200 text-blue-800', InformationCircleIcon, 'text-blue-400'],
  warning: ['bg-amber-50 border-amber-200 text-amber-800', ExclamationIcon, 'text-amber-400'],
  error: ['bg-red-50 border-red-200 text-red-800', XCircleIcon, 'text-red-400'],
}
/** Inline alert box (Tailwind UI alert, as used by the app's notifications). */
export const Notice = ({ type = 'info', title, className, children }) => {
  const [colors, Icon, iconColor] = NOTICES[type]
  return (
    <div className={classNames('flex gap-3 rounded-md border p-4', colors, className)}>
      <Icon className={classNames('h-5 w-5 flex-shrink-0', iconColor)} aria-hidden="true" />
      <div className="min-w-0 text-sm">
        <p className="font-medium">{title}</p>
        {children && <div className="mt-1 break-words opacity-90">{children}</div>}
      </div>
    </div>
  )
}

// ---- Form controls ----

/** Label + control + error (or hint) — the app's modal field layout. */
export const Field = ({ label, htmlFor, error, hint, className, children }) => (
  <div className={className}>
    {label && (
      <label htmlFor={htmlFor} className={LABEL}>
        {label}
      </label>
    )}
    {children}
    {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
  </div>
)

/** Number input keeping the raw string; clamps to min/max on blur (like the old antd InputNumber). */
export const NumberInput = ({ id, value, onChange, min, max, step, addon, placeholder, error, disabled }) => (
  <div className="relative">
    <input
      id={id}
      type="number"
      inputMode="decimal"
      min={min}
      max={max}
      step={step}
      value={isNil(value) ? '' : value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={e => onChange(e.target.value)}
      onBlur={e => !isBlank(e.target.value) && onChange(String(clamp(Number(e.target.value), isNil(min) ? -Infinity : min, isNil(max) ? Infinity : max)))}
      className={classNames(inputClass(error), addon && 'pr-12 rtl:pr-3 rtl:pl-12')}
    />
    {addon && <span className="pointer-events-none absolute inset-y-0 right-0 rtl:right-auto rtl:left-0 flex items-center px-3 text-xs text-gray-500">{addon}</span>}
  </div>
)

/**
 * Wrapper over the app's Selection. options: [{ value, label }].
 * `clearable` adds an empty first option (shown as the placeholder) that maps back to undefined.
 */
export const Select = ({ options, value, onChange, placeholder, multiple, showSearch, clearable, error, disabled }) => {
  const list = map(options, option => ({ id: option.value, name: option.label }))
  if (multiple) {
    return (
      <Selection multiple className="w-full" list={list} value={value || []} title={placeholder} showSearch={showSearch} error={error} disabled={disabled} onChange={onChange} />
    )
  }
  return (
    <Selection
      className="w-full"
      list={clearable ? [{ id: '', name: placeholder }, ...list] : list}
      value={isNil(value) ? '' : value}
      title={placeholder}
      showSearch={showSearch}
      error={error}
      disabled={disabled}
      onChange={item => onChange(item.id === '' ? undefined : item.id)}
    />
  )
}

/** Any / Yes / No select: undefined, true or false. */
export const TriState = ({ value, onChange, placeholder }) => {
  const { t } = useTranslation()
  return (
    <Select
      clearable
      placeholder={placeholder || t('refundEngine.common.any')}
      value={isBoolean(value) ? String(value) : undefined}
      onChange={next => onChange(isNil(next) ? undefined : next === 'true')}
      options={[
        { value: 'true', label: t('refundEngine.common.yes') },
        { value: 'false', label: t('refundEngine.common.no') },
      ]}
    />
  )
}

export const Checkbox = ({ checked, onChange, children }) => (
  <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
    <input type="checkbox" checked={Boolean(checked)} onChange={e => onChange(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-dark-600 focus:ring-dark-500" />
    {children}
  </label>
)

export const ReasonField = ({ id = 'refund-reason', label, value, onChange, error }) => (
  <Field label={label} htmlFor={id} error={error}>
    <textarea id={id} rows={3} value={value || ''} onChange={e => onChange(e.target.value)} className={inputClass(error)} />
  </Field>
)

// ---- Data ----

export const useRefundSettings = () => {
  const [settings, setSettings] = useState(null)
  useEffect(() => {
    apiGet('refund-settings').then(response => response?.success && setSettings(response.data?.settings))
  }, [])
  return [settings, setSettings]
}

export const reasonOptions = (settings, lang) =>
  map(filter(get(settings, 'cancellationReasons', []), 'isActive'), reason => ({
    value: reason.code,
    label: `${reason.code} — ${lang === 'ar' ? reason.ar : reason.en} (${reason.audience})`,
  }))

export const FACT_TRI_STATES = ['carHandedOver', 'wentToOffice', 'noShow', 'officeApproved']

/** Required-field check for the facts form; returns { reason } when missing. */
export const validateFacts = (values, t) => (isBlank(values.reason) ? { reason: t('refundEngine.common.required') } : {})

/** Cancellation facts shared by the simulator and the admin policy cancel (controlled: values + onChange(key, value)). */
export const FactsFields = ({ settings, values, errors = {}, onChange }) => {
  const { t, i18n } = useTranslation()
  const notSet = t('refundEngine.common.notSet')
  return (
    <div className="space-y-4">
      <Field label={t('refundEngine.facts.reason')} error={errors.reason}>
        <Select
          showSearch
          value={values.reason}
          onChange={v => onChange('reason', v)}
          options={reasonOptions(settings, i18n.language)}
          placeholder={notSet}
          error={errors.reason}
        />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label={t('refundEngine.facts.cancelledBy')}>
          <Select
            clearable
            placeholder={notSet}
            value={values.cancelledBy}
            onChange={v => onChange('cancelledBy', v)}
            options={map(CANCELLED_BY, v => ({ value: v, label: t(`refundEngine.cancelledBy.${v}`) }))}
          />
        </Field>
        <Field label={t('refundEngine.facts.deliveryDelayMinutes')} htmlFor="facts-delay">
          <NumberInput id="facts-delay" min={0} value={values.deliveryDelayMinutes} onChange={v => onChange('deliveryDelayMinutes', v)} />
        </Field>
        <Field label={t('refundEngine.facts.issueReportedAt')} htmlFor="facts-issue-at">
          <input
            id="facts-issue-at"
            type="datetime-local"
            value={values.issueReportedAt || ''}
            onChange={e => onChange('issueReportedAt', e.target.value)}
            className={inputClass()}
          />
        </Field>
        {map(FACT_TRI_STATES, key => (
          <Field key={key} label={t(`refundEngine.facts.${key}`)}>
            <TriState placeholder={notSet} value={values[key]} onChange={v => onChange(key, v)} />
          </Field>
        ))}
      </div>
    </div>
  )
}

/** Form values -> API body: dates to ISO, numbers parsed, unset facts dropped. */
export const toFactsPayload = values =>
  pickBy(
    {
      ...values,
      deliveryDelayMinutes: toNumber(values.deliveryDelayMinutes),
      cancelAt: values.cancelAt ? moment(values.cancelAt).toISOString() : undefined,
      issueReportedAt: values.issueReportedAt ? moment(values.issueReportedAt).toISOString() : undefined,
    },
    value => !isNil(value) && value !== ''
  )

// ---- Read-only displays ----

/** Label / value rows. rows: [{ key, label, value, className }] (falsy rows are skipped). */
export const InfoList = ({ title, rows, className }) => (
  <div className={classNames('rounded-lg border border-gray-200 bg-white overflow-hidden', className)}>
    {title && <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wider text-gray-500">{title}</div>}
    <dl className="divide-y divide-gray-100">
      {map(compact(rows), row => (
        <div key={row.key || row.label} className={classNames('flex items-start justify-between gap-4 px-4 py-2 text-sm', row.className)}>
          <dt className="text-gray-500 flex-shrink-0 max-w-[50%]">{row.label}</dt>
          <dd className="min-w-0 text-gray-900 text-right rtl:text-left break-words">{row.value}</dd>
        </div>
      ))}
    </dl>
  </div>
)

const PERCENT_KEYS = ['percent', 'fxFeePercent']
const PAID_KEYS = ['paidTotal', 'walletUsed', 'cardPaid', 'eligiblePaid', 'previouslyRefunded', 'base', 'percent', 'gross']
const DEDUCTION_KEYS = [
  'deliveryFee',
  'deliveryOffice',
  'deliveryOfficeVat',
  'deliveryAjarlee',
  'firstDayPrice',
  'firstDayNet',
  'firstDayVat',
  'fxFeePercent',
  'fxFeeBase',
  'fxFee',
  'bankFee',
]
const SUB_ROWS = ['deliveryOffice', 'deliveryOfficeVat', 'deliveryAjarlee', 'firstDayNet', 'firstDayVat']

/** Refund calculation: paid → base → deductions (grouped) → final amount. */
export const Breakdown = ({ breakdown }) => {
  const { t } = useTranslation()
  if (!breakdown) return null
  const present = list => filter(list, key => !isNil(breakdown[key]))
  const format = key => {
    const value = breakdown[key]
    if (key === 'fxFeeBase') return t(`refundEngine.fxBase.${value}`)
    return includes(PERCENT_KEYS, key) ? `${value}%` : omr(value)
  }
  const row = (key, className, valueClassName = 'text-gray-900') => (
    <div key={key} className={classNames('flex items-start justify-between gap-4 px-4 py-1.5 text-sm', className)}>
      <span className="text-gray-500">{t(`refundEngine.breakdown.${key}`)}</span>
      <span className={classNames('tabular-nums text-right rtl:text-left', valueClassName)}>{format(key)}</span>
    </div>
  )
  const deductions = present(DEDUCTION_KEYS)
  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wider text-gray-500">{t('refundEngine.breakdown.title')}</div>
      <div className="py-1.5">{map(present(PAID_KEYS), key => row(key))}</div>
      {(deductions.length > 0 || !isNil(breakdown.deductions)) && (
        <div className="mx-3 mb-2 rounded-md bg-gray-50 border border-gray-100 py-1.5">
          <div className="px-4 pb-1 text-xs font-medium text-gray-500">{t('refundEngine.rules.columns.deductions')}</div>
          {map(deductions, key => row(key, includes(SUB_ROWS, key) && 'pl-8 rtl:pl-4 rtl:pr-8 text-xs'))}
          {!isNil(breakdown.deductions) && row('deductions', 'border-t border-gray-200 mt-1 pt-2 font-medium', 'text-red-600')}
        </div>
      )}
      {present(['addOn', 'cap']).length > 0 && <div className="py-1.5 border-t border-gray-100">{map(present(['addOn', 'cap']), key => row(key))}</div>}
      {!isNil(breakdown.amount) && (
        <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-gray-200 bg-pink-50">
          <span className="text-sm font-semibold text-gray-900">{t('refundEngine.breakdown.amount')}</span>
          <span className="text-base font-semibold text-primary-600 tabular-nums">{format('amount')}</span>
        </div>
      )}
    </div>
  )
}

const showValue = (value, t) => {
  if (isBoolean(value)) return value ? t('refundEngine.common.yes') : t('refundEngine.common.no')
  if (isNil(value)) return '-'
  return isObject(value) ? JSON.stringify(value) : String(value)
}

/** Result of POST refunds/simulate (simulator tab and policy cancel preview). */
export const SimulationResult = ({ result }) => {
  const { t, i18n } = useTranslation()
  if (!result) return null
  const lang = i18n.language === 'ar' ? 'ar' : 'en'
  const { rule, addOn } = result
  return (
    <div className="space-y-4">
      {result.legacy && (
        <Notice type="warning" title={t('refundEngine.result.legacy')}>
          {result.legacyPolicy ? JSON.stringify(result.legacyPolicy) : null}
        </Notice>
      )}
      {result.customerCancellationBlocked && <Notice type="error" title={t('refundEngine.result.customerBlocked')} />}
      {result.requiresApproval && <Notice type="info" title={t('refundEngine.result.requiresApproval')} />}

      <div className="rounded-lg border border-gray-200 bg-white p-4 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{t('refundEngine.result.amount')}</p>
          <p className="mt-1 text-2xl font-semibold text-primary-600 tabular-nums">{omr(result.amount)}</p>
        </div>
        {rule ? <Badge tone="purple">{`${rule.code} v${rule.version}`}</Badge> : <Badge>{t('refundEngine.result.noRule')}</Badge>}
      </div>

      <InfoList
        rows={[
          {
            label: t('refundEngine.result.rule'),
            value: rule
              ? `${rule.code} v${rule.version} — ${get(rule, `name.${lang}`) || get(rule, 'name.en', '')} (${t('refundEngine.rules.columns.priority')} ${rule.priority}, ${
                  rule.refundPercent
                }%)`
              : t('refundEngine.result.noRule'),
          },
          rule && get(rule, `customerText.${lang}`) && { label: t('refundEngine.rules.fields.customerText'), value: get(rule, `customerText.${lang}`) },
          { label: t('refundEngine.result.refundType'), value: result.refundType ? t(`refundEngine.refundType.${result.refundType}`) : '-' },
          { label: t('refundEngine.result.paid'), value: showValue(result.paid, t) },
          { label: t('refundEngine.result.addOn'), value: addOn ? `${addOn.code} — ${get(addOn, `name.${lang}`) || get(addOn, 'name.en', '')}: ${omr(addOn.amount)}` : '-' },
          { label: t('refundEngine.result.approval'), value: showValue(result.requiresApproval, t) },
          { label: t('refundEngine.result.settingsVersion'), value: showValue(result.settingsVersion, t) },
        ]}
      />
      <Breakdown breakdown={result.breakdown} />
      {result.context && (
        <InfoList
          title={t('refundEngine.result.context')}
          rows={map(keys(result.context), key => ({ key, label: t(`refundEngine.context.${key}`, { defaultValue: key }), value: showValue(result.context[key], t) }))}
        />
      )}
    </div>
  )
}
